// React
import React, { FC, useState, useRef, useContext } from 'react';

// Styling
import injectSheet from 'react-jss';

// Components
import { Input } from 'Components/Inputs';

// Assets
import Lens from 'Assets/ui/lens.svg';

// Utilities
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import { handleAccessibilityClick } from 'Utils/handleAccessibilityClick';
import styleSheet from './styles';
import Animations from '../../../animations';

interface Props {
  name: string;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  classes?: any;
  // Controls whether the search icon is currently usable
  // will otherwise default to other logic.
  searchable?: boolean;
  ariaLabel?: string;
  // Returns any time the input is changed
  onChange: (event: object) => void;
  // Returns on explicity searches (icon is clicked or
  // enter key is pressed)
  onSearch: (event: object) => void;
  disableInputOnly?: boolean;
  width: number;
}

const SearchInput: FC<Props> = (props: Props) => {
  const [value, setValue] = useState('');
  const [fill, setFill] = useState('#6373A4');

  const {
    classes: {
      inputContainer,
      disabledStyle,
      indicator,
      blue,
      clearButton,
      oval,
      cross,
      forward,
      backward,
      errorStyle,
    },
    onChange: propOnChange,
    onSearch: propOnSearch,
    searchable,
    name,
    placeholder,
    ariaLabel,
    error,
    disabled,
    disableInputOnly,
    width,
  } = props;

  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  const searchInput = useRef(null);

  const expandSearchInput = () => {
    Animations.searchInput.in(searchInput, 310);
  };

  const contractSearchInput = () => {
    Animations.searchInput.out(searchInput, width);
  };

  const onFocus = () => {
    // Combined Analytics event tracking
    track('search_started', {
      screen: View(location.pathname),
    });

    expandSearchInput();
    setFill('#085FE5');
  };

  const onBlur = () => {
    if (!value) {
      contractSearchInput();
    }
    setFill('#6373A4');

    // If the value is empty, and the input has lost focus, then reset the table.
    if (value.length === 0) {
      setValue('');
      if (propOnSearch) {
        propOnSearch({ target: { value: '' } });
      }
    }
  };

  const searchExecuted = (searchValue: string, initiatedBy: string) => {
    track('search_executed', {
      screen: View(location.pathname),
      keyword: searchValue,
      initiated_by: initiatedBy,
    });
  };

  const onChange = (event: any) => {
    setValue(event.target.value);
    if (propOnChange) {
      propOnChange(event);
    }
  };

  const onClick = (event) => {
    if (handleAccessibilityClick(event)) {
      // For the Clear X button
      // Combined Analytics event tracking
      track('search_ended', {
        screen: View(location.pathname),
      });
      onChange({ target: { value: '' } });

      if (propOnSearch) propOnSearch({ target: { value: '' } });
      contractSearchInput();
    }
  };

  const handleSearch = (e: any, initiatedBy = 'Keyboard') => {
    // Combined Analytics
    searchExecuted(value, initiatedBy);
    const event = { target: { value } };
    if (propOnSearch) propOnSearch(event);
    e.target.blur();
  };

  const onMouseUp = (e: any) => {
    handleSearch(e, 'Mouse');
  };

  const onKeyUp = (event: any) => {
    if (event.key === 'Enter') {
      handleSearch(event);
    }
  };

  return (
    <>
      <div
        className={`${inputContainer} ${(disabled && disabledStyle) || ''}`}
        ref={searchInput}
        data-testid="searchInput"
      >
        <Lens
          className={`${indicator} ${
            fill === '#085FE5' && searchable && !error && blue
          }`}
          onMouseDown={onMouseUp}
          data-testid="lens"
        />
        <Input
          type="text"
          disabled={disableInputOnly}
          name={name}
          overrideStyles={{
            padding: '0px 35px 0px 33px',
            height: '35px',
            width: `100%`,
            border: '1.5px solid #6373A44D',
            borderRadius: '40px',
            fontSize: '14px',
            color: '6373A4',
          }}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          value={value}
          ariaLabel={ariaLabel}
          onKeyUp={onKeyUp}
        />
        {value && (
          <div
            className={clearButton}
            role="button"
            onClick={onClick}
            onKeyDown={onClick}
            tabIndex={0}
            data-testid="clearButton"
            aria-label="Clear search input"
          >
            <div className={oval}>
              <div className={`${cross} ${forward}`} />
              <div className={`${cross} ${backward}`} />
            </div>
          </div>
        )}
        {error && (
          <div className={errorStyle} aria-live="polite">
            {value && error}
          </div>
        )}
      </div>
    </>
  );
};

SearchInput.defaultProps = {
  classes: {},
  disabled: false,
  searchable: true,
  ariaLabel: '',
  error: undefined,
  disableInputOnly: false,
};

export default injectSheet(styleSheet)(SearchInput);
